import React, { useState, useEffect, useCallback } from 'react';
import {
  XMarkIcon,
  TrashIcon,
  PlusIcon,
  TagIcon,
  PencilIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  BuildingOfficeIcon,
  CurrencyEuroIcon,
  HeartIcon,
  BriefcaseIcon,
  ScaleIcon,
  ShoppingCartIcon,
  HomeIcon,
  TruckIcon,
  WrenchIcon,
  ComputerDesktopIcon,
  UserGroupIcon,
  AcademicCapIcon,
  GlobeAltIcon,
  SparklesIcon,
  BoltIcon,
  BeakerIcon,
  CogIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  QuestionMarkCircleIcon,
  BanknotesIcon,
  TrophyIcon,
  BuildingStorefrontIcon
} from '@heroicons/react/24/outline';
import apiClient from '../../utils/apiClient';
import ConfirmationModal from '../ConfirmationModal';

interface CategoryManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCategoriesUpdated?: () => void;
}

interface Category {
  id: number;
  name: string;
  icon: string;
  steuerliche_sphaere: 'ideell' | 'vermoegen' | 'zweck' | 'wirtschaftlich' | null;
}

type SteuerlicheSphaere = 'ideell' | 'vermoegen' | 'zweck' | 'wirtschaftlich';

const STEUERLICHE_SPHAEREN: { value: SteuerlicheSphaere; label: string; description: string; color: string }[] = [
  { value: 'ideell', label: 'Ideell', description: 'Vereinszweck, gemeinnützige Aktivitäten', color: 'bg-info-100 text-info-800 dark:bg-info-900/30 dark:text-info-300' },
  { value: 'vermoegen', label: 'Vermögen', description: 'Vermögensverwaltung, Zinsen, Mieten', color: 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-300' },
  { value: 'zweck', label: 'Zweckbetrieb', description: 'Sportveranstaltungen, Kurse', color: 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300' },
  { value: 'wirtschaftlich', label: 'Wirtschaftlich', description: 'Wirtschaftlicher Geschäftsbetrieb', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' }
];

const AVAILABLE_ICONS = [
  { name: 'TagIcon', icon: TagIcon, label: 'Tag' },
  { name: 'CurrencyEuroIcon', icon: CurrencyEuroIcon, label: 'Euro' },
  { name: 'BuildingOfficeIcon', icon: BuildingOfficeIcon, label: 'Gebäude' },
  { name: 'HeartIcon', icon: HeartIcon, label: 'Herz' },
  { name: 'BriefcaseIcon', icon: BriefcaseIcon, label: 'Aktentasche' },
  { name: 'ScaleIcon', icon: ScaleIcon, label: 'Waage' },
  { name: 'ShoppingCartIcon', icon: ShoppingCartIcon, label: 'Einkaufswagen' },
  { name: 'HomeIcon', icon: HomeIcon, label: 'Haus' },
  { name: 'TruckIcon', icon: TruckIcon, label: 'LKW' },
  { name: 'WrenchIcon', icon: WrenchIcon, label: 'Werkzeug' },
  { name: 'ComputerDesktopIcon', icon: ComputerDesktopIcon, label: 'Computer' },
  { name: 'UserGroupIcon', icon: UserGroupIcon, label: 'Gruppe' },
  { name: 'AcademicCapIcon', icon: AcademicCapIcon, label: 'Bildung' },
  { name: 'GlobeAltIcon', icon: GlobeAltIcon, label: 'Welt' },
  { name: 'SparklesIcon', icon: SparklesIcon, label: 'Sterne' },
  { name: 'BoltIcon', icon: BoltIcon, label: 'Blitz' },
  { name: 'BeakerIcon', icon: BeakerIcon, label: 'Labor' },
  { name: 'CogIcon', icon: CogIcon, label: 'Einstellungen' },
  { name: 'ShieldCheckIcon', icon: ShieldCheckIcon, label: 'Schutz' },
  { name: 'DocumentTextIcon', icon: DocumentTextIcon, label: 'Dokument' }
];

const CategoryManagementModal: React.FC<CategoryManagementModalProps> = ({
  isOpen,
  onClose,
  onCategoriesUpdated
}) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  // New category state
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryIcon, setNewCategoryIcon] = useState('TagIcon');
  const [newCategorySphaere, setNewCategorySphaere] = useState<SteuerlicheSphaere | ''>('');
  const [showNewIconPicker, setShowNewIconPicker] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editIcon, setEditIcon] = useState('');
  const [editSphaere, setEditSphaere] = useState<SteuerlicheSphaere | ''>('');
  const [showEditIconPicker, setShowEditIconPicker] = useState(false);

  // Delete confirmation
  const [confirmModal, setConfirmModal] = useState({
    visible: false,
    type: 'warning' as 'success' | 'warning' | 'error' | 'question' | 'info',
    title: '',
    message: '',
    variant: 'two' as 'single' | 'two',
    categoryId: null as number | null
  });

  // Help popup
  const [showSphaereHelp, setShowSphaereHelp] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  // Animation
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  // Load categories
  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/finance/categories');
      setCategories(response.data);
      setError(null);
    } catch (err) {
      console.error('Error loading categories:', err);
      setError('Fehler beim Laden der Kategorien');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadCategories();
    }
  }, [isOpen, loadCategories]);

  // Get icon component
  const getIconComponent = (iconName: string) => {
    const iconDef = AVAILABLE_ICONS.find(i => i.name === iconName);
    if (iconDef) {
      const IconComponent = iconDef.icon;
      return <IconComponent className="h-5 w-5" />;
    }
    return <TagIcon className="h-5 w-5" />;
  };

  // Add new category
  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;

    try {
      await apiClient.post('/api/finance/account-categories', {
        category: newCategoryName.trim(),
        icon: newCategoryIcon,
        steuerliche_sphaere: newCategorySphaere || null
      });
      setNewCategoryName('');
      setNewCategoryIcon('TagIcon');
      setNewCategorySphaere('');
      await loadCategories();
      onCategoriesUpdated?.();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Erstellen der Kategorie');
    }
  };

  // Start editing
  const startEditing = (category: Category) => {
    setEditingId(category.id);
    setEditName(category.name);
    setEditIcon(category.icon || 'TagIcon');
    setEditSphaere(category.steuerliche_sphaere || '');
    setShowEditIconPicker(false);
  };

  // Save edit
  const handleSaveEdit = async () => {
    if (!editingId || !editName.trim()) return;

    try {
      await apiClient.put(`/api/finance/categories/${editingId}`, {
        name: editName.trim(),
        icon: editIcon,
        steuerliche_sphaere: editSphaere || null
      });
      setEditingId(null);
      await loadCategories();
      onCategoriesUpdated?.();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Aktualisieren der Kategorie');
    }
  };

  // Cancel edit
  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditIcon('');
    setEditSphaere('');
    setShowEditIconPicker(false);
  };

  // Delete category
  const handleDeleteClick = (category: Category) => {
    if (category.name === 'Mitgliederbeiträge') {
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Nicht möglich',
        message: 'Die Kategorie "Mitgliederbeiträge" kann nicht gelöscht werden.',
        variant: 'single',
        categoryId: null
      });
      return;
    }

    setConfirmModal({
      visible: true,
      type: 'warning',
      title: 'Kategorie löschen?',
      message: `Möchten Sie die Kategorie "${category.name}" wirklich löschen? Dies ist nur möglich, wenn keine Buchungen zugeordnet sind.`,
      variant: 'two',
      categoryId: category.id
    });
  };

  const handleConfirmDelete = async () => {
    if (!confirmModal.categoryId) return;

    const category = categories.find(c => c.id === confirmModal.categoryId);
    if (!category) return;

    try {
      await apiClient.delete(`/api/finance/account-categories/${encodeURIComponent(category.name)}`);
      setConfirmModal({ ...confirmModal, visible: false, categoryId: null });
      await loadCategories();
      onCategoriesUpdated?.();
    } catch (err: any) {
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: err.response?.data?.error || 'Fehler beim Löschen der Kategorie',
        variant: 'single',
        categoryId: null
      });
    }
  };

  // Close handler
  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  if (!isOpen) return null;

  return (
    <>
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
      onClick={handleClose}
    >
      <div
        className="w-full sm:max-w-2xl bg-white dark:bg-[#1c2432] border-t sm:border border-gray-200 dark:border-gray-700 rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col shadow-2xl animate-slide-up sm:animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="w-9 h-9 rounded-xl bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400 grid place-items-center flex-shrink-0">
            <TagIcon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">Kategorien verwalten</h3>
          </div>
          <button
            onClick={handleClose}
            className="w-9 h-9 grid place-items-center rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 text-gray-500 dark:text-gray-400 flex-shrink-0"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg text-error-700 dark:text-error-300 text-sm">
              {error}
            </div>
          )}

          {/* Add new category */}
          <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Neue Kategorie
            </h3>
            <div className="space-y-3">
              <div className="flex gap-2">
                {/* Icon picker button */}
                <div className="relative">
                  <button
                    onClick={() => setShowNewIconPicker(!showNewIconPicker)}
                    className="w-10 h-10 bg-white dark:bg-gray-600 rounded-lg flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-500 transition-colors border border-gray-300 dark:border-gray-500"
                  >
                    {getIconComponent(newCategoryIcon)}
                  </button>
                  {showNewIconPicker && (
                    <div className="absolute top-12 left-0 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-3 z-10 w-64 max-h-48 overflow-y-auto">
                      <div className="grid grid-cols-5 gap-1">
                        {AVAILABLE_ICONS.map((icon) => (
                          <button
                            key={icon.name}
                            onClick={() => {
                              setNewCategoryIcon(icon.name);
                              setShowNewIconPicker(false);
                            }}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            title={icon.label}
                          >
                            <icon.icon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Name input */}
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Kategoriename..."
                  className="flex-1 px-3 py-2 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:text-white text-sm"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                />
              </div>

              {/* Steuerliche Sphäre select with help icon */}
              <div className="relative">
                <div className="flex items-center gap-2">
                  <select
                    value={newCategorySphaere}
                    onChange={(e) => setNewCategorySphaere(e.target.value as SteuerlicheSphaere | '')}
                    className="flex-1 px-3 py-2 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="">Steuerliche Sphäre wählen (optional)</option>
                    {STEUERLICHE_SPHAEREN.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label} - {s.description}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowSphaereHelp(!showSphaereHelp)}
                    className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors flex-shrink-0"
                    title="Was sind steuerliche Sphären?"
                  >
                    <QuestionMarkCircleIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Help Popup */}
                {showSphaereHelp && (
                  <div className="absolute right-0 top-12 z-20 w-80 md:w-96 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="p-4 bg-primary-50 dark:bg-primary-900/30 border-b border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                          <ScaleIcon className="h-5 w-5 text-primary-600" />
                          Steuerliche Sphären (§ 64 AO)
                        </h4>
                        <button
                          onClick={() => setShowSphaereHelp(false)}
                          className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
                        >
                          <XMarkIcon className="h-4 w-4 text-gray-500" />
                        </button>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        Gemeinnützige Vereine müssen Einnahmen in vier Bereiche aufteilen
                      </p>
                    </div>
                    <div className="p-4 space-y-3 max-h-72 overflow-y-auto">
                      {/* Ideell */}
                      <div className="flex gap-3">
                        <div className="w-10 h-10 rounded-lg bg-info-100 dark:bg-info-900/30 flex items-center justify-center flex-shrink-0">
                          <HeartIcon className="h-5 w-5 text-info-600 dark:text-info-400" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white text-sm">Ideeller Bereich</div>
                          <div className="text-xs text-success-600 dark:text-success-400 font-medium">Steuerfrei</div>
                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                            Mitgliedsbeiträge, Spenden, Zuschüsse, Aufnahmegebühren
                          </div>
                        </div>
                      </div>

                      {/* Vermögen */}
                      <div className="flex gap-3">
                        <div className="w-10 h-10 rounded-lg bg-success-100 dark:bg-success-900/30 flex items-center justify-center flex-shrink-0">
                          <BanknotesIcon className="h-5 w-5 text-success-600 dark:text-success-400" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white text-sm">Vermögensverwaltung</div>
                          <div className="text-xs text-amber-600 dark:text-amber-400 font-medium">Begünstigt (7% USt)</div>
                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                            Zinsen, Dividenden, Mieteinnahmen, Pachteinnahmen
                          </div>
                        </div>
                      </div>

                      {/* Zweckbetrieb */}
                      <div className="flex gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                          <TrophyIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white text-sm">Zweckbetrieb</div>
                          <div className="text-xs text-amber-600 dark:text-amber-400 font-medium">Begünstigt (7% USt)</div>
                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                            Startgelder, Kursgebühren, Prüfungsgebühren, Lehrgänge
                          </div>
                        </div>
                      </div>

                      {/* Wirtschaftlich */}
                      <div className="flex gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                          <BuildingStorefrontIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white text-sm">Wirtschaftlicher Betrieb</div>
                          <div className="text-xs text-error-600 dark:text-error-400 font-medium">Steuerpflichtig (19% USt)</div>
                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                            Getränkeverkauf, Vereinsfeste, Werbung, Merchandise
                          </div>
                        </div>
                      </div>

                      {/* Freigrenze Info */}
                      <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                        <div className="text-xs text-amber-800 dark:text-amber-300">
                          <strong>Freigrenze:</strong> Bis 45.000 € Bruttoeinnahmen/Jahr im wirtschaftlichen Bereich = keine KSt/GewSt
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Add button */}
              <button
                onClick={handleAddCategory}
                disabled={!newCategoryName.trim()}
                className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 text-sm font-medium"
              >
                <PlusIcon className="h-4 w-4" />
                Kategorie hinzufügen
              </button>
            </div>
          </div>

          {/* Categories list */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Vorhandene Kategorien ({categories.length})
            </h3>

            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="p-3 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-200 dark:bg-gray-600 rounded-lg animate-pulse" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-4 w-2/3 bg-gray-200 dark:bg-gray-600 rounded animate-pulse" />
                      <div className="h-3 w-16 bg-gray-200 dark:bg-gray-600 rounded animate-pulse" />
                    </div>
                    <div className="flex gap-1">
                      <div className="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded-lg animate-pulse" />
                      <div className="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded-lg animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            ) : categories.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                Keine Kategorien vorhanden
              </div>
            ) : (
              <div className="space-y-2">
                {categories.map((category) => (
                  <div
                    key={category.id}
                    className="p-3 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
                  >
                    {editingId === category.id ? (
                      // Edit mode
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          <div className="relative">
                            <button
                              onClick={() => setShowEditIconPicker(!showEditIconPicker)}
                              className="w-10 h-10 bg-gray-100 dark:bg-gray-600 rounded-lg flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
                            >
                              {getIconComponent(editIcon)}
                            </button>
                            {showEditIconPicker && (
                              <div className="absolute top-12 left-0 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-3 z-10 w-64 max-h-48 overflow-y-auto">
                                <div className="grid grid-cols-5 gap-1">
                                  {AVAILABLE_ICONS.map((icon) => (
                                    <button
                                      key={icon.name}
                                      onClick={() => {
                                        setEditIcon(icon.name);
                                        setShowEditIconPicker(false);
                                      }}
                                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                      title={icon.label}
                                    >
                                      <icon.icon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white text-sm"
                          />
                        </div>
                        <select
                          value={editSphaere}
                          onChange={(e) => setEditSphaere(e.target.value as SteuerlicheSphaere | '')}
                          className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white text-sm"
                        >
                          <option value="">Keine steuerliche Sphäre</option>
                          {STEUERLICHE_SPHAEREN.map((s) => (
                            <option key={s.value} value={s.value}>
                              {s.label} - {s.description}
                            </option>
                          ))}
                        </select>
                        <div className="flex gap-2">
                          <button
                            onClick={handleSaveEdit}
                            className="flex-1 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center gap-1 text-sm"
                          >
                            <CheckIcon className="h-4 w-4" />
                            Speichern
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="px-3 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors text-sm"
                          >
                            Abbrechen
                          </button>
                        </div>
                      </div>
                    ) : (
                      // View mode
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-10 h-10 bg-gray-100 dark:bg-gray-600 rounded-lg flex items-center justify-center flex-shrink-0">
                            {getIconComponent(category.icon)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-gray-900 dark:text-white truncate">
                              {category.name}
                            </div>
                            {category.steuerliche_sphaere && (
                              <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium ${
                                STEUERLICHE_SPHAEREN.find(s => s.value === category.steuerliche_sphaere)?.color || ''
                              }`}>
                                {STEUERLICHE_SPHAEREN.find(s => s.value === category.steuerliche_sphaere)?.label}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => startEditing(category)}
                            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
                            title="Bearbeiten"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(category)}
                            className="p-2 text-gray-500 hover:text-error-600 hover:bg-error-50 dark:hover:bg-error-900/20 rounded-lg transition-colors"
                            title="Löschen"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Compact hint */}
          <div className="mt-4 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <QuestionMarkCircleIcon className="h-4 w-4 flex-shrink-0" />
            <span>
              Klicke auf das <strong>?</strong>-Icon beim Dropdown für Details zu den steuerlichen Sphären.
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2 flex-wrap">
          <button
            onClick={handleClose}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm font-semibold"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>

      {/* Confirmation Modal */}
      {confirmModal.visible && (
        <ConfirmationModal
          visible={confirmModal.visible}
          type={confirmModal.type}
          title={confirmModal.title}
          message={confirmModal.message}
          variant={confirmModal.variant}
          onClose={() => setConfirmModal({ ...confirmModal, visible: false, categoryId: null })}
          buttons={confirmModal.variant === 'two' ? {
            cancel: {
              text: 'Abbrechen',
              onPress: () => setConfirmModal({ ...confirmModal, visible: false, categoryId: null })
            },
            confirm: {
              text: 'Löschen',
              onPress: handleConfirmDelete
            }
          } : undefined}
          button={confirmModal.variant === 'single' ? {
            text: 'OK',
            onPress: () => setConfirmModal({ ...confirmModal, visible: false, categoryId: null })
          } : undefined}
        />
      )}
    </>
  );
};

export default CategoryManagementModal;
